import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Shield, Smartphone, Check, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Configurar2FA() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  const isMandatory = location.state?.mandatory || false;
  const setupToken = location.state?.setupToken || null;

  useEffect(() => {
    startSetup();
  }, []);

  const startSetup = async () => {
    try {
      // Use token from state if available (fallback for cookie issues)
      const headers = setupToken 
        ? { 'X-Setup-Token': setupToken }
        : {};
      
      const response = await axios.post(`${API_URL}/api/security/2fa/setup`, {}, {
        withCredentials: true,
        headers
      });
      setSetupData(response.data);
      // Save recovery codes from setup (they are generated here, not in verify)
      if (response.data.recovery_codes) {
        setRecoveryCodes(response.data.recovery_codes);
      }
    } catch (error) {
      console.error('Error starting 2FA setup:', error);
      toast.error('Erro ao iniciar configuração do 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (verifyCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setVerifyLoading(true);
    try {
      // Use token from state if available (fallback for cookie issues)
      const headers = setupToken 
        ? { 'X-Setup-Token': setupToken }
        : {};
      
      const response = await axios.post(`${API_URL}/api/security/2fa/verify-setup`, 
        { code: verifyCode },
        { withCredentials: true, headers }
      );
      
      toast.success('2FA ativado com sucesso!');
      // Show recovery codes (saved from setup)
      setShowRecoveryCodes(true);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleContinue = async () => {
    // Login again to get proper session
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data) {
        setUser(response.data);
        navigate('/entrada', { replace: true });
      }
    } catch (error) {
      // If not authenticated, redirect to login
      navigate('/login', { replace: true });
    }
  };

  const copyRecoveryCodes = () => {
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Códigos copiados!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (showRecoveryCodes) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-stone-900 rounded-xl border border-stone-800 p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-stone-100 mb-2">2FA Ativado!</h1>
            <p className="text-stone-400">Salve seus códigos de recuperação</p>
          </div>

          <div className="bg-stone-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-400 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Guarde estes códigos em local seguro
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
              {recoveryCodes.map((code, index) => (
                <div key={index} className="bg-stone-700 rounded px-2 py-1 text-stone-300">
                  {code}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={copyRecoveryCodes}
              variant="outline"
              className="w-full border-stone-700"
            >
              Copiar Códigos
            </Button>
            <Button
              onClick={handleContinue}
              className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900"
            >
              Continuar para o App
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-stone-900 rounded-xl border border-stone-800 p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-stone-100 mb-2">
            Configurar Autenticação
          </h1>
          <p className="text-stone-400">
            {isMandatory 
              ? 'A autenticação de dois fatores é obrigatória para sua segurança'
              : 'Adicione uma camada extra de proteção'
            }
          </p>
        </div>

        {/* Instructions */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-amber-500 text-sm font-bold">1</span>
            </div>
            <div>
              <p className="text-stone-200 text-sm font-medium">Baixe um app autenticador</p>
              <p className="text-stone-500 text-xs">Google Authenticator, Authy ou Microsoft Authenticator</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-amber-500 text-sm font-bold">2</span>
            </div>
            <div>
              <p className="text-stone-200 text-sm font-medium">Escaneie o QR Code</p>
              <p className="text-stone-500 text-xs">Ou digite a chave manual no app</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-amber-500 text-sm font-bold">3</span>
            </div>
            <div>
              <p className="text-stone-200 text-sm font-medium">Digite o código gerado</p>
              <p className="text-stone-500 text-xs">O código muda a cada 30 segundos</p>
            </div>
          </div>
        </div>

        {/* QR Code */}
        {setupData && setupData.qr_code && (
          <div className="bg-white rounded-lg p-4 mb-6 flex justify-center">
            <img 
              src={setupData.qr_code}
              alt="QR Code para autenticação"
              className="w-48 h-48"
            />
          </div>
        )}

        {/* Manual Key */}
        {setupData && (
          <div className="bg-stone-800 rounded-lg p-3 mb-6">
            <p className="text-xs text-stone-400 mb-1">Chave manual:</p>
            <p className="font-mono text-sm text-stone-200 break-all select-all">
              {setupData.secret}
            </p>
          </div>
        )}

        {/* Verification Form */}
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-300 mb-2">
              <Smartphone className="w-4 h-4 inline mr-2" />
              Código do Autenticador
            </label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="text-center text-2xl tracking-widest bg-stone-800 border-stone-700"
              autoFocus
            />
          </div>

          <Button
            type="submit"
            disabled={verifyLoading || verifyCode.length !== 6}
            className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900"
          >
            {verifyLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Verificar e Ativar
          </Button>
        </form>

        {!isMandatory && (
          <button
            onClick={() => navigate(-1)}
            className="w-full mt-4 text-stone-500 hover:text-stone-400 text-sm"
          >
            Voltar
          </button>
        )}
      </div>
    </div>
  );
}
